import * as v from 'valibot';

/**
 * Every audited action in reef. The set is closed on purpose: an admin filtering
 * the log should be able to enumerate what can appear, and a new kind of event
 * should be a deliberate schema change rather than a free-text string.
 */
export const AuditEventType = v.picklist([
  'step.started',
  'step.finished',
  'step.paused',
  'step.input',
  'step.output',
  'tool.call',
  'tool.result',
  'policy.decision',
  'approval.requested',
  'approval.granted',
  'approval.rejected',
  'connection.used',
  'connection.denied',
  'workflow.created',
  'workflow.updated',
  'workflow.duplicated',
  'workflow.deleted',
  'workflow.shared',
  'run.started',
  'run.finished',
  'run.forked',
  'runtime.escalated',
  /*
   * The LLM gateway's two outcomes worth a row.
   *
   * A pass is not audited: it is every request, and a log where the
   * overwhelming majority of entries say "nothing happened" is one nobody
   * filters successfully. The ledger in `reef_llm_request` has the passes, with
   * the numbers; these two are the events a person goes looking for.
   */
  'llm.blocked',
  'llm.redacted',
  /*
   * The operational controls, which are audited for a different reason than
   * everything above them.
   *
   * The rest of this list records what an *agent* did. These four record what a
   * person did to the agents — stopping the factory, pausing a project — and
   * the question they answer is asked in the retrospective rather than at the
   * time: who stopped this, when, and why. A control whose use is not recorded
   * is one nobody can reconstruct the morning after.
   */
  'factory.stopped',
  'factory.released',
  'project.paused',
  'project.released',
  /*
   * A detection firing is audited as well as stored, because the audit trail is
   * where somebody reconstructing an incident is already looking — and a
   * detection that lived only in its own table would be found only by somebody
   * who already suspected it.
   */
  'detection.fired',
  /*
   * Which machine a container step got, warm or cold.
   *
   * Audited rather than only counted, because the question it answers is asked
   * about one run — "why did this take four minutes to start" — and a pool-wide
   * hit rate cannot answer it.
   */
  'environment.leased',
  /*
   * How a machine came up: the boot mode, and what each `.reef/` script did.
   *
   * A row rather than a counter for the same reason `environment.leased` is,
   * one layer down. "Warm" stopped being one thing the moment provisioning
   * became something reef does: a lease that restored source and then spent
   * ninety seconds in `pnpm install` and a lease that skipped it entirely are
   * both warm, and only this row says which happened. It is also the only
   * durable record of a `setup.sh` that failed survivably — the step ran, its
   * output looks ordinary, and the reason its build was odd is here.
   */
  'environment.booted',
  /*
   * Which repositories a step was checked out into, and which it was refused.
   *
   * The refusals are why this is a row rather than a counter. A step that was
   * declared against three repositories and ran against two produced a change
   * that *looks* complete, and the only durable record of the missing third —
   * and of whether it was missing because somebody's access was revoked that
   * morning — is here. It is also where the answer to "which repositories did
   * this run touch" comes from without reading every tool call.
   */
  'workspace.prepared',
  'schedule.created',
  'schedule.fired',
  /*
   * An automation taking itself out of service, and a person putting it back.
   *
   * Audited rather than only stored on the row, because the pause is the one
   * event here that happens while nobody is looking: the row says an automation
   * is paused *now*, and the audit trail is what says it stopped at 02:14 on
   * the third failure — which is the sentence somebody needs when they are
   * working out what else broke at 02:14. Resuming is audited because it is an
   * explicit act: an automation never un-pauses itself, so there is always a
   * person to name.
   */
  'automation.paused',
  'automation.resumed',
  /*
   * Standing an agent identity up, and breaking it.
   *
   * Both are audited because an agent is a principal a grant can name: creating
   * one creates something that can be given access, and revoking one is
   * offboarding. Without these two rows the trail can say what the bot did and
   * never say who made it or who stopped it — which is the half of an identity
   * feature that gets asked about after an incident rather than before one.
   * Group membership changes ride on `workflow.shared`, because what they alter
   * is a grant.
   */
  'agent.created',
  'agent.revoked',
]);
export type AuditEventType = v.InferOutput<typeof AuditEventType>;

/**
 * Which identity actually performed the action. Recording this separately from
 * the acting user is the whole point: "PR merged" means something different when
 * it ran under a GitHub App token than under Alice's OAuth token.
 */
export const IdentityRef = v.object({
  /**
   * `agent` is a workspace agent identity, and it is its own kind rather than a
   * `service` row with a nicer id. `service` says "a credential out of the
   * deployment's bindings was used" and answers *which credential*; `agent`
   * says "this principal acted" and answers *who*. Collapsing them would make a
   * filter for everything one bot did indistinguishable from a filter for
   * everything one token did, which is the distinction the identity exists to
   * draw.
   *
   * On `agent.created` and `agent.revoked` it names the agent the row is
   * *about* rather than the admin who acted — the admin is in `actorUserId`,
   * and these two are the only rows where the two differ. Filtering the trail
   * to one agent has to return the row that made it and the row that broke it,
   * or the identity's own history starts at its first tool call.
   */
  kind: v.picklist(['user', 'github_app', 'service', 'system', 'agent']),
  id: v.string(),
  /** Connection id the credential came from, when applicable. */
  connectionId: v.optional(v.nullable(v.string())),
  display: v.optional(v.nullable(v.string())),
});
export type IdentityRef = v.InferOutput<typeof IdentityRef>;

export const AuditEvent = v.object({
  id: v.string(),
  type: AuditEventType,
  at: v.string(),
  workflowId: v.nullable(v.string()),
  runId: v.nullable(v.string()),
  stepId: v.nullable(v.string()),
  conversationId: v.optional(v.nullable(v.string())),
  actorUserId: v.nullable(v.string()),
  /**
   * The acting person's address, resolved when the log is read rather than
   * copied in when the row is written.
   *
   * The identity column exists to answer "whose access was this?", and a
   * better-auth user id does not answer it for a human reading the screen.
   * Resolved on read so a changed address is not frozen into a permanent
   * record, and left null for an actor who no longer exists — which is itself
   * worth seeing.
   */
  actorEmail: v.optional(v.nullable(v.string())),
  identity: v.optional(v.nullable(IdentityRef)),
  /**
   * Small structured summary, always stored in D1 so it is queryable.
   * Anything large (full tool output, full model input) goes to R2 and is
   * referenced by `payloadRef`.
   */
  summary: v.optional(v.nullable(v.string())),
  detail: v.optional(v.record(v.string(), v.unknown())),
  /** R2 object key holding the full payload, when one was written. */
  payloadRef: v.optional(v.nullable(v.string())),
});
export type AuditEvent = v.InferOutput<typeof AuditEvent>;

/** What the policy engine decided about one tool call, recorded verbatim. */
export const PolicyDecision = v.object({
  policyIds: v.array(v.string()),
  tool: v.string(),
  allowed: v.boolean(),
  /** Human-readable list: "truncated 10000 rows to 100", "dropped column ssn". */
  actions: v.array(v.string()),
  blockedReason: v.optional(v.nullable(v.string())),
  rowsIn: v.optional(v.nullable(v.number())),
  rowsOut: v.optional(v.nullable(v.number())),
  bytesIn: v.optional(v.nullable(v.number())),
  bytesOut: v.optional(v.nullable(v.number())),
});
export type PolicyDecision = v.InferOutput<typeof PolicyDecision>;
